import { classnames } from '@aibees/shared/utils';
import { Box, Skeleton } from '@mui/material';
import { memo } from 'react';
import * as PlanCardSX from '../../sx';
import * as SX from './sx';
import type { PlanCardSkeletonProps as Props } from './types';

// Approximates the typical loaded card's inclusion section so the skeleton
// reserves the same vertical space and the layout doesn't jump when data
// arrives. The real catalog renders ~7 grouped sections with 2–4 rows each.
const INCLUSION_GROUPS: { rows: string[] }[] = [
  { rows: ['70%', '80%', '55%'] },
  { rows: ['65%', '85%', '60%'] },
  { rows: ['75%', '70%'] },
  { rows: ['80%', '60%', '85%'] },
  { rows: ['65%', '75%', '55%', '70%'] },
  { rows: ['80%', '60%', '85%', '70%'] },
  { rows: ['70%', '65%'] },
];

export const PlanCardSkeleton = memo(
  ({ className, variant = 'default', isMostPopular }: Props) => {
    const isCompact = variant === 'compact';

    return (
      <Box sx={PlanCardSX.wrapper}>
        <Box
          className={classnames('PlanCardSkeleton', className)}
          sx={
            isCompact
              ? PlanCardSX.compactRoot(isMostPopular)
              : PlanCardSX.root({ isMostPopular })
          }
        >
          <Box sx={SX.wrapper}>
            <Box sx={SX.headRow}>
              <Skeleton variant="text" width={96} height={28} />
              {!isCompact && (
                <Skeleton variant="rounded" width={88} height={22} />
              )}
            </Box>

            <Skeleton variant="text" width="55%" height={48} />

            <Box>
              <Skeleton variant="text" width={72} height={20} />
              <Skeleton variant="rounded" width={132} height={28} />
            </Box>

            <Box>
              <Skeleton variant="text" width="80%" height={18} />
              <Skeleton variant="text" width={70} height={18} />
            </Box>

            <Skeleton variant="rounded" width="100%" height={40} />
            <Skeleton variant="rounded" width="100%" height={36} />

            {!isCompact && (
              <>
                <Box sx={SX.inclusionList}>
                  {INCLUSION_GROUPS.map((group, gi) => (
                    <Box key={gi} sx={SX.group}>
                      <Box sx={SX.groupHeader}>
                        <Skeleton variant="text" width={120} height={20} />
                      </Box>
                      {group.rows.map((w, ri) => (
                        <Box key={ri} sx={SX.inclusionRow}>
                          <Skeleton variant="circular" width={18} height={18} />
                          <Skeleton variant="text" width={w} height={18} />
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>

                <Box sx={SX.compareSlot}>
                  <Skeleton variant="rounded" width="100%" height={1} />
                  <Skeleton variant="rounded" width="100%" height={32} />
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    );
  }
);

PlanCardSkeleton.displayName = 'PlanCardSkeleton';
